<template>
    <DetailHeader pretitle="aqfer Graph Manager" :editable="false" secondaryTitle="Job Stack Deployment"
        :title="deployment?.name" :customMode="editConfig" customModeText="Edit" customBtnClass="btn btn-info"
        @customClick="handleEditClick" @closeClick="$router.push(`/${cid}/agm/sandbox/${sandboxId}/deployments`)" />
    <div class="container-fluid">
        <div class="row">
            <div class="col-4">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-header-title">Deployment Details</h4>
                    </div>
                    <div class="card-body">
                        <div v-if="loading">
                            <div class="d-flex justify-content-center align-items-center">
                                <div class="spinner-border spinner-border-sm me-2" role="status"
                                    style="width: 1em; height: 1em;">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p style="padding-top: 15px;">Loading deployment details...</p>
                            </div>
                        </div>
                        <div v-else>
                            <div v-if="deployment">
                                <div class="list-group list-group-flush" style="font-size: 0.85rem;">
                                    <div class="list-group-item" v-if="deployment?.id">
                                        <div class="row">
                                            <div class="col">
                                                <span>Deployment ID</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <strong>{{ deployment.id }}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.sandbox">
                                        <div class="row">
                                            <div class="col">
                                                <span>Sandbox</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <span>{{ deployment.sandbox }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.job_stack">
                                        <div class="row">
                                            <div class="col">
                                                <span>Job Stack</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <span>{{ deployment.job_stack }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.deployed_by">
                                        <div class="row">
                                            <div class="col">
                                                <span>Deployed By</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <strong>{{ deployment.deployed_by }}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.deployed_at">
                                        <div class="row">
                                            <div class="col">
                                                <span>Deployed At</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <strong>{{ DateTimeFormat(deployment.deployed_at) }}</strong>
                                                <span class="ms-2 badge badge-secondary">{{
                                                    convertToRelativeTime(deployment.deployed_at) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-center">
                                No data found!
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-8">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-header-title">Pipeline Flow</h4>
                    </div>
                    <div class="card-body">
                        <div v-if="isLoadingPipeline || loading">
                            <div class="d-flex justify-content-center align-items-center">
                                <div class="spinner-border spinner-border-sm me-2" role="status"
                                    style="width: 1em; height: 1em;">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p style="padding-top: 15px;">Loading pipeline details...</p>
                            </div>
                        </div>
                        <div v-else>
                            <pipelineGraph v-if="pipelineJobDetails.length > 0" :pipelineDetails="pipelineJobDetails"
                                :pipelineName="pipelineName">
                            </pipelineGraph>
                            <div v-else class="text-center">
                                No data found!
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-header p-4 ">
                        <h4 class="card-header-title d-flex align-items-center">
                            <span>Jobs Created</span>
                            <span class="fa fa-circle-info text-muted ms-2"
                                title="List of jobs created in this deployment" data-bs-toggle="tooltip"
                                data-bs-placement="right" style="font-size: 0.8rem;"></span>
                        </h4>
                    </div>
                    <div class="card-body">
                        <div v-if="deployment?.jobs && deployment.jobs.length > 0">
                            <div class="list-group list-group-flush pb-1">
                                <div v-for="(job, index) in deployment?.jobs" :key="index"
                                    class="list-group-item job-list-item">
                                    <button class="btn btn-link text-decoration-none link-primary job-link-btn"
                                        @click="redirectToSanboxJob(job)">
                                        <strong>{{ job.name }}</strong>
                                        <span class="badge badge-secondary ms-2" v-if="job.kind == 'PIPELINE'">{{
                                            job.kind }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center">
                            No data found!
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DetailHeader from "../../components/DetailHeader.vue";
import pipelineGraph from "./pipelineGraph.vue";
import { DateTimeFormat, relativeTime } from '../../utils/commonFunction';
import { mapActions } from "vuex";
import dayjs from 'dayjs';
import axios from 'axios';
export default {
    name: "IDR Job Stack Deployment Detail",
    components: { DetailHeader, pipelineGraph },
    data() {
        return {
            id: this.$route.params.id,
            cid: this.$route.params.cid,
            sandboxId: this.$route.params.sandboxId,
            deployment: null,
            pipelineJobDetails: [], // Store the job details here
            isLoadingPipeline: false,
            pipelineName: null,
            loading: false,
        }
    },
    async beforeMount() {
        await this.fetchDeployment();
        await this.fetchPipelineDetails();
    },
    computed: {
        jobNames() {
            if (this.deployment?.jobs) {
                try {
                    return this.deployment.jobs.map(job => job.name);
                } catch (e) {
                    return [];
                }
            }
            return []
        },
        editConfig() {
            // Check if the sandbox and pipeline job details are available
            if (!this.deployment?.sandbox || !this.pipelineJobDetails || this.pipelineJobDetails.length === 0) {
                return false;
            }
            // Check if the sandbox is in the devSandboxes array from the store
            const devSandboxes = this.$store.state.sandbox.devSandboxes || [];
            return devSandboxes.some(sandbox => sandbox.name === this.deployment.sandbox);
        },
    },
    methods: {
        ...mapActions({
            readDeployment: "amdp/readJobStackDeployment",
            readFlowSet: "amdp/readFlowSet",
        }),
        DateTimeFormat,
        async fetchDeployment() {
            let response;
            this.loading = true;
            try {
                response = await this.readDeployment({
                    sandboxName: this.sandboxId,
                    deploymentId: this.id
                });
                this.deployment = response.data;
            } catch (error) {
                console.error("Error fetching job stack deployments: ", error);
                let err = error.response?.data?.error ? error.response.data.error : error.message;
                this.$store.dispatch("addToast", {
                    message: err || "Something went wrong when fetching job stack deployment. Please contact support.",
                    type: "error",
                });
            } finally {
                this.loading = false;
            }
        },
        convertToRelativeTime(timestamp) {
            return relativeTime(Date.now(), dayjs(timestamp));
        },
        redirectToSanboxJob(job) {
            this.$router.push({
                name: "Sandbox Job Configuration",
                params: {
                    sandboxId: `${this.deployment.sandbox}`,
                    jobId: `${job.name}`
                },
            });
        },
        handleEditClick() {
            // Open modal with job details for editing
            this.$store.dispatch('showModal', {
                component: 'EditJobStackDeploymentConfigs',
                data: {
                    jobNames: this.jobNames,
                    jobs: this.pipelineJobDetails,
                    deployment: this.deployment,
                    sandboxName: this.deployment?.sandbox || this.sandboxId,
                    cid: this.cid,
                    screensize: 'xl',
                    onRefreshJobs: this.fetchPipelineDetails
                }
            });
        },
        async fetchPipelineDetails() {
            if (!this.jobNames || this.jobNames.length === 0) {
                this.pipelineJobDetails = [];
                return;
            }

            this.isLoadingPipeline = true;
            try {
                const details = await Promise.all(
                    this.jobNames.map(async (jobId) => {
                        try {
                            const response = await this.readSandboxJobDetails(this.deployment.sandbox, jobId);
                            if (response?.data && response.data.kind == "PIPELINE") {
                                this.pipelineName = response.data.name;
                            }
                            return {
                                jobId: jobId,
                                jobDetails: response?.data || null,
                                error: null
                            };
                        } catch (error) {
                            return {
                                jobId: jobId,
                                jobDetails: null,
                                error: error.response?.data?.error || error.message
                            };
                        }
                    })
                );
                this.pipelineJobDetails = details;
                console.log('Pipeline details fetched:', this.pipelineJobDetails);
            } catch (error) {
                console.error('Error fetching pipeline details:', error);
            } finally {
                this.isLoadingPipeline = false;
            }
        },
        async readSandboxJobDetails(sandboxName, jobName) {
            return axios.get(this.$store.state.lakeviewUrl + `/v1/cids/${this.cid}/sandboxes/${sandboxName}/jobs/${jobName}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            })
        },
    },
}
</script>

<style scoped>
.job-list-item {
    padding: 0.75rem 1rem;
    border: 1.5px solid #e9ecef;
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
    transition: all 0.2s ease-in-out;
    background-color: #f8f9fa;
}

.job-list-item:hover {
    background-color: #fff;
    border-color: #0d6efd;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.job-list-item:last-child:hover {
    border-bottom-color: #0d6efd;
}

.job-list-item:last-child {
    margin-bottom: 0;
    border-bottom: 1.5px solid #e9ecef;
}

.job-link-btn {
    width: 100%;
    text-align: left;
    padding: 0;
    color: #0d6efd;
    font-size: 0.95rem;
    transition: color 0.2s ease-in-out;
}

.job-link-btn:hover {
    color: #0a58ca;
}

.job-link-btn i {
    font-size: 0.9rem;
    opacity: 0.8;
}

.job-link-btn strong {
    font-weight: 500;
}
</style>